import React from 'react';

import { formatBytes, formatDuration } from './formatBytes';
import type { DropzonePreviewProps, FileUploadStatus } from './types';

// Pause, cross and play glyphs, drawn in #333333.
const ICONS = {
  cancel: {
    backgroundImage:
      'url(data:image/svg+xml;base64,PHN2ZyB2aWV3Qm94PSIwIDAgOCAxNCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSIjMzMzMzMzIj48cGF0aCBkPSJNMSwxNCBDMC40LDE0IDAsMTMuNiAwLDEzIEwwLDEgQzAsMC40IDAuNCwwIDEsMCBDMS42LDAgMiwwLjQgMiwxIEwyLDEzIEMyLDEzLjYgMS42LDE0IDEsMTQgWiIgaWQ9IlBhdGgiPjwvcGF0aD48cGF0aCBkPSJNNywxNCBDNi40LDE0IDYsMTMuNiA2LDEzIEw2LDEgQzYsMC40IDYuNCwwIDcsMCBDNy42LDAgOCwwLjQgOCwxIEw4LDEzIEM4LDEzLjYgNy42LDE0IDcsMTQgWiIgaWQ9IlBhdGgiPjwvcGF0aD48L2c+PC9zdmc+Cg==)',
  },
  remove: {
    backgroundImage:
      'url(data:image/svg+xml;base64,PHN2ZyB2aWV3Qm94PSIwIDAgMTQgMTQiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGcgdHJhbnNmb3JtPSJ0cmFuc2xhdGUoLTUuMCwgMC4wKSIgZmlsbD0iIzMzMzMzMyI+PGcgdHJhbnNmb3JtPSJ0cmFuc2xhdGUoNC4wLCAwLjApIj48cG9seWdvbiBwb2ludHM9IjcuNzE5IDQuOTY0IDEyLjY5MiAwLjAxNyAxNC4zODkgMS43MTUgOS40MTIgNi42NjYgMTQuMzU0IDExLjYzNCAxMi42NTcgMTMuMzMxIDYuMDE3IDYuNjU3IDcuNzE1IDQuOTYwIj48L3BvbHlnb24+PHBvbHlnb24gcG9pbnRzPSI3LjYxMiA0Ljk2NCA3LjYxNiA0Ljk2MCA5LjMxMyA2LjY1NyAyLjY3NCAxMy4zMzEgMC45NzcgMTEuNjM0IDUuOTE5IDYuNjY2IDAuOTQyIDEuNzE1IDIuNjM5IDAuMDE3Ij48L3BvbHlnb24+PC9nPjwvZz48L3N2Zz4K)',
  },
  restart: {
    backgroundImage:
      'url(data:image/svg+xml;base64,PHN2ZyB2aWV3Qm94PSIwIDAgMTEgMTUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGc+PHBhdGggZD0iTTAuNSwxNC45IEMwLjIsMTQuNyAwLDE0LjQgMCwxNCBMMCwyIEMwLDEuNiAwLjIsMS4zIDAuNSwxLjEgQzAuOCwwLjkgMS4yLDAuOSAxLjUsMS4xIEwxMC41LDcuMSBDMTAuOCw3LjMgMTAuOSw3LjYgMTAuOSw3LjkgQzEwLjksOC4yIDEwLjcsOC41IDEwLjUsOC43IEwxLjUsMTQuNyBDMS40LDE0LjkgMC44LDE1LjEgMC41LDE0LjkgWiBNMiwzLjkgTDIsMTIuMiBMOC4yLDguMSBMMiwzLjkgWiI+PC9wYXRoPjwvZz48L3N2Zz4K)',
  },
};

const FAILED: FileUploadStatus[] = ['error_upload_params', 'exception_upload', 'error_upload'];
const RESTARTABLE: FileUploadStatus[] = [...FAILED, 'aborted'];
const BUSY: FileUploadStatus[] = ['preparing', 'getting_upload_params', 'uploading'];

/**
 * One row per file: an image thumbnail or `name, size`, a progress bar while uploading, and
 * cancel, remove and restart icons. Its English text has no translation, so a translated surface
 * passes its own `PreviewComponent`.
 */
export const DefaultFilePreview = ({
  className,
  imageClassName,
  style,
  imageStyle,
  fileWithMeta: { cancel, remove, restart },
  meta: { name = '', percent = 0, size = 0, previewUrl, status, duration },
  isUpload,
  canCancel,
  canRemove,
  canRestart,
  extra: { minSizeBytes },
}: DropzonePreviewProps): JSX.Element => {
  let title = `${name || '?'}, ${formatBytes(size)}`;
  if (duration) title = `${title}, ${formatDuration(duration)}`;

  if (status === 'error_file_size') {
    return (
      <div className={className} style={style}>
        <span className="dzu-previewFileNameError">{title}</span>
        <span>{size < minSizeBytes ? 'File too small' : 'File too big'}</span>
        {canRemove && <span className="dzu-previewButton" style={ICONS.remove} onClick={remove} />}
      </div>
    );
  }

  if (FAILED.includes(status)) title = `${title} (upload failed)`;
  if (status === 'aborted') title = `${title} (cancelled)`;

  return (
    <div className={className} style={style}>
      {previewUrl && <img className={imageClassName} style={imageStyle} src={previewUrl} alt={title} title={title} />}
      {!previewUrl && <span className="dzu-previewFileName">{title}</span>}

      <div className="dzu-previewStatusContainer">
        {isUpload && <progress max={100} value={status === 'done' || status === 'headers_received' ? 100 : percent} />}

        {status === 'uploading' && canCancel && (
          <span className="dzu-previewButton" style={ICONS.cancel} onClick={cancel} />
        )}
        {!BUSY.includes(status) && canRemove && (
          <span className="dzu-previewButton" style={ICONS.remove} onClick={remove} />
        )}
        {RESTARTABLE.includes(status) && canRestart && (
          <span className="dzu-previewButton" style={ICONS.restart} onClick={restart} />
        )}
      </div>
    </div>
  );
};
